/**
 * O REGISTRO das conversas vivas — cada uma com módulo Vuex e stream próprios.
 *
 * ★ O QUE ISTO RESOLVE (owner, 18/09/2026)
 *
 * Hoje o chat vive uma conversa por vez, por superfície: `messages[]` é uma
 * lista só e o `_streamSeq` do `APIChat` é por INSTÂNCIA — abrir um turno novo
 * ABORTA o anterior. Daí os três sintomas medidos na tela: dois cartões de
 * `/panorama` empilhados, o painel "que não fez nada" e a mesma resposta com
 * estrutura diferente.
 *
 * Com o registro, cada conversa tem seu namespace, sua instância e seu stream.
 * Duas conversas rodando ao mesmo tempo param de se atropelar por construção —
 * e o `desfecho=abandonado_pelo_cliente` que o backend vinha registrando some
 * do caso comum, porque ninguém mais aborta o vizinho.
 *
 * ★ POR QUE A CHAVE É UM ID LOCAL, E NÃO O `conversation_id` DO BACKEND
 *
 * Uma conversa nova NÃO TEM id até o primeiro turno voltar — o front manda
 * `conversation_id: null` e o backend cria. Se a chave fosse o id do servidor,
 * o registro precisaria RENOMEAR o módulo Vuex no meio do voo (registrar o
 * novo, copiar o state, desregistrar o velho) — com um stream escrevendo nele.
 *
 * O id local nasce antes do primeiro caractere e nunca muda. O id do servidor
 * vive DENTRO do módulo (`selectedConversation`), que é onde o resto do código
 * já o procura. Zero renomeação, zero janela de corrida.
 */
import { APIChat } from '@modules/aawz-intelligence-v2/api/apiChat'
import {
  criarModuloDaConversa,
  namespaceDaConversa,
} from '@modules/aawz-intelligence-v2/store/conversa-viva'
import store from '@state/store'

/** Status de uma conversa, na régua que o consultor lê na tela. */
export const STATUS = {
  VAZIA: 'vazia',
  PROCESSANDO: 'processando',
  AGUARDANDO_VOCE: 'aguardando_voce',
  FINALIZADA: 'finalizada',
}

/**
 * O id de uma conversa NOVA — um UUID v4 canônico, gerado aqui no front.
 *
 * ★ POR QUE O FRONT GERA (decisão do owner, 18/09/2026)
 *
 * A primeira versão disto devolvia um id LOCAL (`c1-abc123`), que resolvia a
 * corrida de chave mas não servia para URL. O owner apontou o que faltava:
 * *"até para que cada conversa consiga ter um url direto para ela"*.
 *
 * Com o id nascendo aqui, ele existe antes do primeiro caractere: dá para pôr
 * na URL na hora, sobrevive a um F5 e pode ser mandado para outra pessoa. Se
 * esperássemos o backend, a conversa ficaria sem endereço até o primeiro turno
 * VOLTAR — e é justamente no começo que o consultor troca de aba.
 *
 * O backend já aceita (`usecase_ia_crm_chat._uuid_da_conversa_pedido`): id
 * livre vira o id da conversa; id que já existe no escritório retoma; id
 * malformado é ignorado sem erro.
 *
 * ★ A FORMA IMPORTA, E É POR ISSO QUE ISTO NÃO É UM `Math.random` SOLTO
 *
 * O validador do backend é `str(UUID(x)) == x.lower()` — a MESMA régua do
 * Postgres. Ele aceita só a forma CANÔNICA minúscula: 8-4-4-4-12, com o `4` da
 * versão e o variante em `[89ab]`. Um gerador que devolvesse maiúsculas, ou que
 * esquecesse os bits de versão/variante, seria **descartado em silêncio** — o
 * front pediria um id, receberia outro, e a URL que ele já montou apontaria
 * para uma conversa que não existe. É um defeito que não dá erro em lugar
 * nenhum, e por isso tem teste próprio.
 */
export function novoIdDeConversa() {
  const nativo = (typeof crypto !== 'undefined' && crypto && crypto.randomUUID)
    ? crypto.randomUUID()
    : _uuidV4DeFallback()
  return String(nativo).toLowerCase()
}

/**
 * UUID v4 sem `crypto.randomUUID`.
 *
 * ★ O fallback NÃO é zelo excessivo: `randomUUID` exige contexto seguro
 * (https ou localhost) e não existe em jsdom antigo. Num ambiente sem ele, sem
 * fallback, a função devolveria `undefined` — e o turno sairia com
 * `conversation_id: 'undefined'`, que o backend descarta em silêncio.
 *
 * Usa `getRandomValues` quando existe (aleatoriedade criptográfica) e cai para
 * `Math.random` só em último caso. Os dois caminhos põem os bits de versão (4)
 * e variante (10xx) no lugar — sem eles a string PARECE um UUID e é recusada
 * pelo `uuid.UUID()` do backend.
 */
function _uuidV4DeFallback() {
  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && crypto && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40  // versão 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80  // variante RFC 4122
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'))
  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

/**
 * O status de uma conversa, derivado do estado dela. Função PURA — recebe o
 * state, devolve a régua. Testável sem Vuex, sem store, sem componente.
 *
 * ★ A ORDEM É A DECISÃO, e ela não é arbitrária: `aguardando_voce` vem ANTES de
 * `processando` porque é o único estado em que a conversa não anda sozinha. Se
 * as duas valessem ao mesmo tempo, dizer "processando" esconderia do consultor
 * justamente a conversa que precisa dele — que é o oposto do que o painel
 * existe para fazer.
 */
export function derivarStatus(estado) {
  if (!estado) return STATUS.VAZIA

  const temPropostaAberta = Object.values(estado.propostasAbertas || {})
    .some((p) => p && p.estado === 'aberta')
  if (estado.pendingConfirmation || temPropostaAberta) return STATUS.AGUARDANDO_VOCE

  if (estado.chatLoading) return STATUS.PROCESSANDO

  const temMensagem = (estado.messages || []).length > 0
  return temMensagem ? STATUS.FINALIZADA : STATUS.VAZIA
}

/**
 * Este módulo Vuex já está registrado?
 *
 * ★ `store.hasModule` NÃO SERVE AQUI: ele chegou no Vuex 3.2.0 e este projeto
 * roda **3.1.2** (medido em `node_modules/vuex/package.json`; `grep hasModule`
 * no bundle devolve ZERO). Chamá-lo lança `TypeError: hasModule is not a
 * function` — foi o que os 8 testes que tocam o store acusaram na primeira
 * rodada.
 *
 * A leitura abaixo é o que o próprio `hasModule` do 3.2 faz: consultar o mapa
 * de filhos do módulo raiz. É API privada, então vem cercada — qualquer
 * mudança de forma do Vuex cai no `catch` e o registro segue pelo que ele
 * mesmo sabe (`_vivas`), que é a fonte de verdade desta aba.
 */
export function moduloRegistrado(namespace, storeDaAba) {
  const alvo = storeDaAba || store
  try {
    const filhos = alvo._modules && alvo._modules.root && alvo._modules.root._children
    if (filhos) return Object.prototype.hasOwnProperty.call(filhos, namespace)
  } catch (e) { /* forma do Vuex mudou: cai no fallback abaixo */ }
  return Array.from(_vivas.values()).some((v) => v.namespace === namespace)
}

/** As conversas vivas desta aba, por id local. */
const _vivas = new Map()

/**
 * Amarra a conversa viva ao id que o SERVIDOR deu a ela.
 *
 * ★ DECISÃO DO OWNER (19/09/2026): *"Iniciou conversa → recebo ack no backend
 * → retorno id da conversa do backend enquanto prossigo com resposta; dessa
 * forma uma coisa fica amarrada na outra"*.
 *
 * O ACK já existe dos dois lados do `POST /runs`: o backend devolve
 * `value.id_conversa` na resposta SÍNCRONA (antes do SSE), e
 * `apiChatModoAgente` já o usa para selecionar a conversa. O que faltava era
 * ESTA ponta — a conversa viva registrar que aquele id do servidor é dela.
 *
 * Sem a amarração, medido na tela em 18/09: o servidor batizava a conversa com
 * id próprio, a rota passava a segui-lo, e a barra — vendo na URL um id que
 * nenhuma viva reconhecia — abria uma aba a mais. Dois envios viravam QUATRO
 * abas, e a resposta aparecia na aba errada.
 *
 * O `idLocal` continua sendo a chave: o namespace Vuex e o motor nasceram com
 * ele e não se mexem. `idServidor` é só o apelido pelo qual a URL e a lista de
 * conversas do backend chamam a mesma conversa.
 */
export function amarrarAoServidor(idLocal, idServidor) {
  const viva = _vivas.get(idLocal)
  if (!viva || !idServidor) return false
  // ⚠️ [20/09/2026] A amarração é REESCRITA quando a aba passa a mostrar OUTRA
  // conversa, e isso não é afrouxar a guarda — é a premissa dela que mudou.
  //
  // Ela nasceu recusando o segundo id porque, em 18/09, um segundo id para a
  // mesma viva só podia ser defeito ("o servidor criando OUTRA conversa"). Hoje
  // é estado LEGÍTIMO e comum: a aba é um CONTÊINER, e a conversa dentro dela
  // troca em dois gestos do dia — abrir uma conversa de "Recentes" na mesma
  // aba, e "nova conversa" (que desde 20/09 abre conversa NOVA no servidor, em
  // vez de reusar o id local já gasto — ver `view-chat.vue::idVivoDisponivel`).
  //
  // Com a recusa, `viva.idServidor` ficava apontando para a conversa ANTERIOR:
  // `vivaPorIdServidor` não achava a aba para o id que está na URL e
  // `retomarConversasVivas` abria uma aba A MAIS — exatamente o defeito das
  // "quatro abas" que a amarração existe para impedir, entrando pela outra
  // porta. O `idLocal` continua sendo a chave (namespace e motor não se
  // mexem); o `idServidor` é só o apelido corrente.
  // ⚠️ [20/09/2026, review do #2342] O apelido é ÚNICO entre as vivas: se
  // OUTRA aba já respondia por este id (abrir a MESMA conversa de "Recentes"
  // numa segunda aba), ela o perde aqui. Sem isto, duas vivas carregavam o
  // mesmo `idServidor`, `vivaPorIdServidor` (um `find`) devolvia a PRIMEIRA, e
  // o remount com `/home-ia/c/<id>` punha o foco na aba errada — com as duas
  // streamando na mesma conversa do servidor, cada uma com seu `messages[]`.
  _vivas.forEach((outra) => {
    if (outra !== viva && outra.idServidor === idServidor) {
      outra.idServidor = null
    }
  })
  viva.idServidor = idServidor
  return true
}

/** A conversa viva que responde por um id do servidor, ou `undefined`. */
export function vivaPorIdServidor(idServidor) {
  if (!idServidor) return undefined
  return Array.from(_vivas.values()).find((v) => v.idServidor === idServidor)
}

/**
 * Abre (ou recupera) uma conversa viva. Idempotente: chamar duas vezes com o
 * mesmo `idLocal` devolve a MESMA instância — nunca registra o módulo de novo,
 * que apagaria o state de quem está no meio de um turno.
 */
export function abrirConversaViva({
  idLocal,
  endpoints,
  contextoDeTela,
  store: storeDaAba,
} = {}) {
  const id = idLocal || novoIdDeConversa()
  if (_vivas.has(id)) return _vivas.get(id)

  const alvo = storeDaAba || store
  const namespace = namespaceDaConversa(id)
  if (!moduloRegistrado(namespace, alvo)) {
    alvo.registerModule(namespace, criarModuloDaConversa())
  }

  const viva = {
    idLocal: id,
    namespace,
    // O id que o SERVIDOR deu a esta conversa, quando já deu (ver
    // `amarrarAoServidor`). `null` até o primeiro ACK.
    idServidor: null,
    // ⚠️ [20/09/2026] O id LOCAL já virou conversa no servidor? É fato da ABA,
    // não da view: a view não remonta na troca de aba, e um latch em `data()`
    // era rearmado a cada troca — a aba que tinha feito "nova conversa" (ou
    // apagado a dela) voltava a mandar o `idLocal` GASTO, e o servidor ou
    // CONTINUAVA a conversa abandonada em silêncio, ou recusava com "Esta
    // conversa foi removida". Quem marca é o watcher de `selectedConversation`
    // (`view-chat.vue`); quem lê é `_idDaConversaParaOTurno`.
    //
    // ⚠️ Não dá para derivar de `idServidor`: desde 20/09 ele volta a `null`
    // quando outra viva assume o mesmo apelido.
    idLocalGasto: false,
    // ★ O store em que ESTA conversa vive. Em produção é sempre o único
    // (`@state/store`), e o default existe para os call-sites que não
    // conhecem store nenhum; quem monta componente passa o `this.$store` dele,
    // que é o mesmo objeto — e assim o registro para de depender de um
    // singleton importado para ser exercitável fora do app montado.
    store: alvo,
    apiChat: new APIChat({ storeNamespace: namespace, endpoints, contextoDeTela }),
  }
  _vivas.set(id, viva)
  return viva
}

/**
 * Fecha uma conversa viva: para o stream e devolve a memória.
 *
 * ★ A ORDEM IMPORTA. Abortar ANTES de desregistrar: um stream vivo escrevendo
 * num namespace que acabou de sumir lança dentro do laço de leitura, num ponto
 * onde ninguém trata — e o erro apareceria como falha do turno seguinte, não
 * deste. Nunca levanta: fechar conversa é limpeza, e limpeza que estoura é
 * pior que memória retida.
 */
export function fecharConversaViva(idLocal) {
  const viva = _vivas.get(idLocal)
  if (!viva) return false

  try {
    if (viva.apiChat && viva.apiChat._abortarStreamAtivo) viva.apiChat._abortarStreamAtivo()
  } catch (e) {
    console.warn('[ia-crm] falha ao abortar o stream da conversa ' + idLocal, e)
  }
  try {
    const alvo = viva.store || store
    if (moduloRegistrado(viva.namespace, alvo)) alvo.unregisterModule(viva.namespace)
  } catch (e) {
    console.warn('[ia-crm] falha ao desregistrar o módulo da conversa ' + idLocal, e)
  }
  _vivas.delete(idLocal)
  return true
}

/** Os ids locais das conversas vivas, na ordem de abertura. */
export function conversasVivas() {
  return Array.from(_vivas.keys())
}

/** A conversa viva de um id local, ou `undefined`. */
export function conversaViva(idLocal) {
  return _vivas.get(idLocal)
}

/**
 * O painel: uma linha por conversa viva, com o status já derivado. É o que
 * alimenta a lista lateral, as abas do topo e o badge global — as três
 * superfícies que o owner pediu, todas lendo do MESMO lugar.
 */
export function panoramaDasConversas() {
  return conversasVivas().map((idLocal) => {
    const viva = _vivas.get(idLocal)
    const estado = (viva.store || store).state[viva.namespace]
    const selecionada = estado && estado.selectedConversation
    return {
      idLocal,
      namespace: viva.namespace,
      idConversa: (selecionada && selecionada.id) || null,
      titulo: (selecionada && selecionada.topic) || null,
      status: derivarStatus(estado),
    }
  })
}

/** Quantas conversas em cada status — a fonte do badge global. */
export function contagemPorStatus() {
  const zero = {
    [STATUS.VAZIA]: 0,
    [STATUS.PROCESSANDO]: 0,
    [STATUS.AGUARDANDO_VOCE]: 0,
    [STATUS.FINALIZADA]: 0,
  }
  return panoramaDasConversas().reduce((acc, linha) => {
    acc[linha.status] += 1
    return acc
  }, zero)
}

/** Só para os testes: esvazia o registro sem tocar no store real. */
export function _limparRegistroParaTeste() {
  _vivas.clear()
}
